import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { meApi, slotsApi } from "../../api";
import Sidebar from "../layout/Sidebar";
import BookAppointment from "./book/BookAppointment";
import classes from "./Appointments.module.scss";

// ── Types ─────────────────────────────────────────────────
type SlotStatus = "booked" | "open" | "blocked" | "done";

type Slot = {
  id: string;
  date: string;       // "YYYY-MM-DD"
  time: string;       // "9:00 AM"
  timeVal: string;
  duration: number;
  status: SlotStatus;
  client?: string;
  clientId?: string;
  type?: string;
};

// Map slot status → appointment view status
type AptStatus = "upcoming" | "completed" | "cancelled";
const toAptStatus = (s: SlotStatus): AptStatus => {
  if (s === "done")    return "completed";
  if (s === "booked")  return "upcoming";
  return "cancelled";
};

type FilterTab = "All" | "Upcoming" | "Completed";
const FILTERS: FilterTab[] = ["All", "Upcoming", "Completed"];

const STATUS_LABEL: Record<AptStatus, string> = {
  upcoming:  "Upcoming",
  completed: "Completed",
  cancelled: "Cancelled",
};

const CalIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const Spinner = () => (
  <div style={{ display: "flex", justifyContent: "center", padding: 60 }}>
    <div style={{ width: 32, height: 32, border: "3px solid #EBF7EB", borderTopColor: "#4A7C40", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
    <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
  </div>
);

// Format "YYYY-MM-DD" + "9:00 AM" → "Apr 8, 2026 · 9:00 AM"
const formatDateTime = (date: string, time: string) => {
  if (!date) return time;
  const [y, m, d] = date.split("-").map(Number);
  const label = new Date(y, m - 1, d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  return `${label} · ${time}`;
};

// ── Component ─────────────────────────────────────────────
const Appointments = () => {
  const { user }  = useAuth();
  const navigate  = useNavigate();

  const [slots,      setSlots]      = useState<Slot[]>([]);
  const [loading,    setLoading]    = useState(true);
  const [isActive,   setIsActive]   = useState(false);
  const [filter,     setFilter]     = useState<FilterTab>("All");
  const [showBook,   setShowBook]   = useState(false);
  const [cancelling,   setCancelling]   = useState<string | null>(null);
  const [confirmingId, setConfirmingId] = useState<string | null>(null);

  const fetchSlots = useCallback(async () => {
    setLoading(true);
    try {
      const [data, profileData] = await Promise.all([
        meApi.getAppointments(),
        meApi.profile().catch(() => null),
      ]);
      const appointments: Slot[] = data.appointments ?? [];
      setSlots(appointments);
      const profileActive = profileData?.user?.status === "active";
      const hasCompleted  = appointments.some(s => s.status === "done");
      setIsActive(profileActive || hasCompleted);
    } catch (err) {
      console.error("Fetch appointments error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSlots(); }, [fetchSlots]);

  const userName = user?.name ?? "—";

  const displayed = slots.filter(s => {
    if (filter === "All")       return true;
    if (filter === "Upcoming")  return s.status === "booked";
    if (filter === "Completed") return s.status === "done";
    return true;
  });

  const upcoming    = slots.filter(s => s.status === "booked").length;
  const completed   = slots.filter(s => s.status === "done").length;
  const hasUpcoming = upcoming > 0;

  const handleBookClose = () => {
    setShowBook(false);
    fetchSlots();
  };

  const handleCancel = async (slotId: string) => {
    setCancelling(slotId);
    setConfirmingId(null);
    try {
      await slotsApi.cancel(slotId);
      fetchSlots();
    } catch (err) {
      console.error("Cancel error:", err);
    } finally {
      setCancelling(null);
    }
  };

  return (
    <div className={classes.layout}>
      <Sidebar />

      <div className={classes.main_wrap}>
        {/* Topbar */}
        <header className={classes.topbar}>
          <div className={classes.breadcrumb}>
            <button type="button" className={classes.breadcrumb_btn} onClick={() => navigate("/dashboard")}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M15 18l-6-6 6-6" />
              </svg>
              Dashboard
            </button>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#aaa" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 18l6-6-6-6" />
            </svg>
            <span className={classes.breadcrumb_current}>Appointments</span>
          </div>
          <div className={classes.topbar_right}>
            <div className={classes.avatar}>{userName[0]?.toUpperCase()}</div>
          </div>
        </header>

        <main className={classes.content}>
          <div className={classes.page_header}>
            <div>
              <h1 className={classes.page_title}>My Appointments</h1>
              <p className={classes.page_sub}>Your sessions with RD. Tatiana Karam</p>
            </div>
            <div className={classes.book_btn_wrap}>
              <button type="button"
                className={`${classes.book_btn} ${hasUpcoming ? classes.book_btn_blocked : ""}`}
                onClick={() => !hasUpcoming && setShowBook(true)}
                disabled={hasUpcoming}
                title={hasUpcoming ? "You already have an upcoming appointment" : undefined}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
                Book Appointment
              </button>
              {hasUpcoming && (
                <div className={classes.blocked_msg}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                  You already have an upcoming appointment. Cancel it first to book a new one.
                </div>
              )}
            </div>
          </div>

          {/* Stats */}
          <div className={classes.stats_row}>
            <div className={classes.stat_card}>
              <span className={classes.stat_num}>{slots.length}</span>
              <span className={classes.stat_label}>Total</span>
            </div>
            <div className={`${classes.stat_card} ${classes.stat_upcoming}`}>
              <span className={classes.stat_num}>{upcoming}</span>
              <span className={classes.stat_label}>Upcoming</span>
            </div>
            <div className={`${classes.stat_card} ${classes.stat_completed}`}>
              <span className={classes.stat_num}>{completed}</span>
              <span className={classes.stat_label}>Completed</span>
            </div>
          </div>

          {/* Filter tabs */}
          <div className={classes.filter_bar}>
            {FILTERS.map(f => (
              <button key={f} type="button"
                className={`${classes.filter_tab} ${filter === f ? classes.filter_tab_active : ""}`}
                onClick={() => setFilter(f)}
              >{f}</button>
            ))}
          </div>

          {/* List */}
          {loading ? <Spinner /> : (
            <div className={classes.apt_list}>
              {displayed.length === 0 && (
                <div className={classes.empty}>
                  {filter === "All"
                    ? "No appointments scheduled yet."
                    : `No ${filter.toLowerCase()} appointments.`}
                </div>
              )}
              {displayed.map(slot => {
                const aptStatus = toAptStatus(slot.status);
                return (
                  <div key={slot.id} className={`${classes.apt_card} ${classes[`apt_${aptStatus}`]}`}>
                    <div className={classes.apt_icon_wrap}><CalIcon /></div>
                    <div className={classes.apt_info}>
                      <span className={classes.apt_type}>{slot.type ?? "Appointment"}</span>
                      <span className={classes.apt_meta}>
                        {formatDateTime(slot.date, slot.time)}
                        {slot.duration ? ` · ${slot.duration} min` : ""}
                      </span>
                    </div>
                    <span className={`${classes.apt_badge} ${classes[`badge_${aptStatus}`]}`}>
                      {STATUS_LABEL[aptStatus]}
                    </span>
                    {slot.status === "booked" && (
                      confirmingId === slot.id ? (
                        <div className={classes.inline_confirm}>
                          <span>Cancel?</span>
                          <button type="button" className={classes.confirm_yes} onClick={() => handleCancel(slot.id)} disabled={cancelling === slot.id}>
                            {cancelling === slot.id ? "…" : "Yes"}
                          </button>
                          <button type="button" className={classes.confirm_no} onClick={() => setConfirmingId(null)}>No</button>
                        </div>
                      ) : (
                        <button type="button" className={classes.cancel_apt_btn} onClick={() => setConfirmingId(slot.id)}>
                          Cancel
                        </button>
                      )
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </main>
      </div>

      {showBook && (
        <div className={classes.modal_overlay} onClick={handleBookClose}>
          <div className={classes.modal_box} onClick={e => e.stopPropagation()}>
            <button type="button" className={classes.modal_close} onClick={handleBookClose} aria-label="Close">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
            <BookAppointment onClose={handleBookClose} isActive={isActive} />
          </div>
        </div>
      )}
    </div>
  );
};

export default Appointments;
